"use client";

import type { ComponentProps, FC, PropsWithChildren } from "react";

import { ConfigProvider } from "@jti/app-context/ConfigProvider";
import { Toaster } from "@jti/core-components/components/Toaster";
import { useDirection } from "@jti/fe-common";
import { SessionExpiry } from "@jti/ui/components/SessionExpiry";

import { GTMInitStateProvider } from "@/context/GTMContext";
import { useLocale } from "@/context/LocaleContext";
import type { PageConfigContext } from "@/context/PageConfigContext";
import { PageConfigProvider } from "@/context/PageConfigContext";
import { PageConfigEditorSync } from "@/context/PageConfigContext/PageConfigEditorSync";
import { getLangWithCountryCode } from "@/utils/getLangWithCountryCode";

import { ClientEffects } from "./ClientEffects";

type ConfigProviderProps = ComponentProps<typeof ConfigProvider>;

type PageModelProvidersProps = {
  locale: string;
  urlPath?: string;
  pageConfig: PageConfigContext;
  configuration: ConfigProviderProps["config"];
  dictionary: ConfigProviderProps["translations"];
  builderLivePageConfigModel?: string;
};

export const ClientProviders: FC<PropsWithChildren<PageModelProvidersProps>> = ({
  locale,
  urlPath,
  pageConfig,
  dictionary,
  configuration,
  builderLivePageConfigModel,
  children,
}) => {
  const localeContext = useLocale();
  const direction = useDirection();

  const langWithCountryCode = getLangWithCountryCode(localeContext.currentLang, localeContext.territory);

  const pageConfigSyncKey = `${locale}/${urlPath}`;
  const pageConfigProvider =
    builderLivePageConfigModel !== undefined ? (
      <PageConfigEditorSync
        basePageConfig={pageConfig}
        pageConfigSyncKey={pageConfigSyncKey}
        builderLivePageConfigModel={builderLivePageConfigModel}
      >
        {children}
      </PageConfigEditorSync>
    ) : (
      <PageConfigProvider value={pageConfig}>{children}</PageConfigProvider>
    );

  return (
    <ConfigProvider
      locale={locale}
      langWithCountryCode={langWithCountryCode}
      config={configuration}
      translations={dictionary}
      direction={direction}
    >
      <GTMInitStateProvider>{pageConfigProvider}</GTMInitStateProvider>
      <Toaster closeButtonLabel={dictionary?.["common.close"]} />
      <SessionExpiry />
      <ClientEffects />
    </ConfigProvider>
  );
};
